import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  Building2,
  CalendarClock,
  CheckCircle,
  CreditCard,
  FileSignature,
  KeyRound,
  LayoutDashboard,
  MessagesSquare,
  ShieldCheck,
  Wrench,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const modules = [
  {
    icon: LayoutDashboard,
    title: 'Owner and manager dashboard',
    body: 'Occupancy, arrears, upcoming renewals and open maintenance in one view, instead of four spreadsheets and a group chat.',
  },
  {
    icon: KeyRound,
    title: 'Tenant portal',
    body: 'Residents see their lease, balance and documents, submit requests, and stop calling the office to ask questions the portal can answer.',
  },
  {
    icon: Wrench,
    title: 'Maintenance and ticketing',
    body: 'Requests with photos, routed to the right contractor, with status the tenant can follow and a history you can audit later.',
  },
  {
    icon: CreditCard,
    title: 'Rent collection and billing',
    body: 'Recurring charges, partial payments, late fees and receipts, wired to a real payment provider rather than tracked by hand.',
  },
  {
    icon: FileSignature,
    title: 'Documents and e-signature',
    body: 'Leases, addenda and notices generated from templates, signed digitally, and filed against the right unit automatically.',
  },
  {
    icon: CalendarClock,
    title: 'Applications and screening flow',
    body: 'Public listing pages, online applications, document upload, and a review pipeline that moves applicants through to approval.',
  },
  {
    icon: MessagesSquare,
    title: 'Messaging and notices',
    body: 'Announcements, renewal reminders and rent notices sent from the system, with a record of who was told what and when.',
  },
  {
    icon: ShieldCheck,
    title: 'Roles and audit trail',
    body: 'Owners, managers, staff and tenants each see only their own scope, enforced in the database — with every consequential action logged.',
  },
];

const whoItsFor = [
  'Independent landlords outgrowing spreadsheets',
  'Property management companies with 50–2,000 units',
  'Student and co-living operators',
  'Short-term and serviced apartment operators',
  'Commercial and mixed-use portfolios',
  'HOA and community associations',
];

const PropertyManagementPortals = () => (
  <div className="min-h-screen pt-16 bg-gradient-to-b from-primary/10 to-background dark:from-primary/5">
    <Helmet>
      <title>Property Management Portal Development | Omnicraft</title>
      <meta
        name="description"
        content="Custom rental and property management portals for US operators: tenant portals, maintenance ticketing, rent collection, e-signature leases and role-based dashboards. Book a free scoping call."
      />
      <meta
        name="keywords"
        content="property management portal development, rental portal software, custom tenant portal, property management software development, landlord portal, rent collection software, maintenance ticketing system, real estate web application development USA"
      />
      <link
        rel="canonical"
        href="https://www.omnicraftservices.com/software/property-management-portals"
      />
    </Helmet>

    {/* Hero */}
    <section className="py-12 md:py-20">
      <div className="container-custom max-w-4xl mx-auto text-center">
        <Link
          to="/services"
          className="inline-flex items-center text-primary mb-6 hover:underline text-sm md:text-base"
        >
          <ArrowLeft size={16} className="mr-2" /> Back to Services
        </Link>
        <div className="flex flex-col items-center justify-center mb-4">
          <Building2 size={48} className="text-primary mb-2" />
          <h1 className="text-4xl md:text-5xl font-bold mb-2">
            Rental &amp; Property Management Portals
          </h1>
        </div>
        <p className="text-lg md:text-xl text-slate-700 dark:text-slate-300 mb-6">
          Off-the-shelf property software either does too little or charges per unit for
          ninety features you will never open. We build the portal your operation actually
          runs on — owned by you, priced once.
        </p>
        <Button asChild size="lg" className="rounded-full px-8">
          <Link to="/contact">Book a free scoping call</Link>
        </Button>
      </div>
    </section>

    {/* Modules */}
    <section className="py-12 md:py-16">
      <div className="container-custom max-w-6xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-primary">
          What a portal usually includes
        </h2>
        <p className="text-center text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-10">
          You do not need all of this on day one. Most builds start with two or three of
          these and grow.
        </p>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {modules.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="h-full border-primary/10">
              <CardContent className="pt-6">
                <Icon className="text-primary mb-3" size={26} />
                <h3 className="text-base font-semibold mb-2">{title}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  {body}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>

    {/* Why custom */}
    <section className="py-12 md:py-16 bg-slate-50 dark:bg-slate-900/50 border-y border-primary/10">
      <div className="container-custom max-w-4xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-primary">
          Why operators build instead of subscribe
        </h2>
        <div className="grid gap-6 md:grid-cols-2">
          {[
            {
              title: 'Per-unit pricing stops scaling with you',
              body: 'Subscription platforms charge more precisely as you grow. A portal you own costs what it costs, and the hundredth unit is free.',
            },
            {
              title: 'Your process is not their process',
              body: 'Every operator has rules the big platforms will not bend to — deposit handling, split ownership, unusual lease terms. Custom software bends.',
            },
            {
              title: 'Your data stays yours',
              body: 'You hold the database and the accounts from day one. No export fees, no lock-in, no waiting on a roadmap for a field you need.',
            },
            {
              title: 'It connects to what you already use',
              body: 'Accounting, payment providers, e-signature, storage and calendars — integrated properly rather than re-keyed by staff.',
            },
          ].map(({ title, body }) => (
            <div
              key={title}
              className="rounded-xl border border-primary/10 bg-white dark:bg-slate-900 p-6"
            >
              <h3 className="text-lg font-semibold mb-2">{title}</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Who it's for */}
    <section className="py-12 md:py-16">
      <div className="container-custom max-w-4xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-primary">
          Who we build these for
        </h2>
        <ul className="grid gap-3 sm:grid-cols-2">
          {whoItsFor.map((item) => (
            <li
              key={item}
              className="flex items-start gap-2 rounded-lg border border-primary/10 bg-white/60 dark:bg-slate-900/40 p-4"
            >
              <CheckCircle size={18} className="text-primary mt-0.5 shrink-0" />
              <span className="text-sm text-slate-700 dark:text-slate-300">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>

    {/* Capability proof */}
    <section className="py-12 md:py-16 bg-slate-50 dark:bg-slate-900/50 border-y border-primary/10">
      <div className="container-custom max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary">
          The hard parts, already solved
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mb-8 max-w-2xl mx-auto">
          A rental portal is mostly multi-tenant permissions, recurring billing and
          document handling done correctly. Those are the same problems we shipped in{' '}
          <a
            href="https://hrflare.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary font-medium hover:underline"
          >
            HR FLARE
          </a>
          , our own platform — and in the subscription products in our portfolio.
        </p>
        <ul className="grid gap-3 sm:grid-cols-2 text-left">
          {[
            'Multi-tenant data isolation enforced in the database',
            'Recurring billing and subscription management with Stripe',
            'Role-based access across owner, staff and end-user tiers',
            'Document storage, generation and e-signature flows',
            'Audit logging on every consequential action',
            'Approval and request workflows with notifications',
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <CheckCircle size={18} className="text-primary mt-0.5 shrink-0" />
              <span className="text-sm text-slate-700 dark:text-slate-300">{item}</span>
            </li>
          ))}
        </ul>
        <div className="mt-8">
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/portfolio">See the portfolio</Link>
          </Button>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-12 md:py-16 bg-primary text-white">
      <div className="container-custom max-w-3xl mx-auto text-center">
        <h2 className="mb-4 text-3xl font-bold">
          Tell us how your portfolio runs today
        </h2>
        <p className="mb-8 text-white/90">
          Bring the spreadsheet, the software you are frustrated with, or just the list of
          things that go wrong. We will scope what a portal would take — and say so if you
          are better off staying where you are.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" variant="secondary" className="rounded-full px-8">
            <Link to="/contact">Book a scoping call</Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="rounded-full px-8 border-white text-white hover:bg-white hover:text-primary"
          >
            <Link to="/software/ai-workflow-automation">AI workflow automation</Link>
          </Button>
        </div>
      </div>
    </section>
  </div>
);

export default PropertyManagementPortals;
